/**
 * File guard utilities for import operations
 *
 * This module contains all file type detection and filtering logic
 * used across import operations. Extracted from utils/import.ts for
 * better organization and reusability.
 */

/**
 * Detects if file content appears to be binary by checking for:
 * - NUL bytes (common in binary files)
 * - High ratio of non-printable characters
 */
export function isBinaryContent(buffer: ArrayBuffer): boolean {
  const bytes = new Uint8Array(buffer)
  const sampleSize = Math.min(16_384, bytes.length) // Check first 16KB
  let nonPrintableCount = 0

  for (let index = 0; index < sampleSize; index++) {
    const byte = bytes[index]

    // Check for NUL byte (definitive binary indicator)
    if (byte === 0) {
      return true
    }

    // Count non-printable characters (excluding common whitespace)
    if (byte < 32 && byte !== 9 && byte !== 10 && byte !== 13) {
      nonPrintableCount++
    }
  }

  // If more than 30% non-printable characters, likely binary
  return nonPrintableCount / sampleSize > 0.3
}

/**
 * Detects if a file path should be skipped (hidden files, system files)
 *
 * @param path - File path to check
 * @returns true if file should be skipped, false otherwise
 */
export function shouldSkipFile(path: string): boolean {
  const segments = path.split('/')

  // Skip hidden files/folders (starting with .)
  if (segments.some(segment => segment.startsWith('.'))) {
    return true
  }

  // Skip common system files
  const systemFiles = new Set([
    'Thumbs.db',
    'desktop.ini',
    '__MACOSX',
    '.DS_Store',
    '.localized'
  ])

  return segments.some(segment => systemFiles.has(segment))
}

/**
 * Detects if a file is a markdown file based on extension
 *
 * @param path - File path to check
 * @returns true if file is markdown, false otherwise
 */
export function isMarkdownFile(path: string): boolean {
  const extensions = ['.md', '.markdown', '.mdown', '.mkd', '.txt']
  return extensions.some(extension => path.toLowerCase().endsWith(extension))
}

/**
 * Detects if a file is a potential attachment (non-markdown, non-system file)
 *
 * @param path - File path to check
 * @returns true if file is an attachment, false otherwise
 */
export function isAttachmentFile(path: string): boolean {
  // Skip markdown files
  if (isMarkdownFile(path)) {
    return false
  }

  // Skip system/hidden files
  if (shouldSkipFile(path)) {
    return false
  }

  // Common attachment extensions
  const attachmentExtensions = [
    // Images
    '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.svg', '.webp', '.ico',
    // Documents
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt',
    // Archives
    '.zip', '.rar', '.7z', '.tar', '.gz',
    // Audio/Video
    '.mp3', '.mp4', '.avi', '.mov', '.wav', '.flac',
    // Other
    '.json', '.xml', '.csv', '.log'
  ]

  const extension = '.' + (path.split('.').pop()?.toLowerCase() ?? '')
  return attachmentExtensions.includes(extension)
}

import { filenameToDate } from '../note'

/**
 * Returns true when the file name can be parsed as a calendar note
 * (day, week, month, quarter or year).
 *
 * Supports:
 *  - YYYY-MM-DD.md
 *  - YYYYMMDD.md
 *  - YYYY-Www.md
 *  - YYYY-MM.md
 *  - YYYY-Qn.md
 *  - YYYY.md
 *
 * @param path - File path to check
 * @returns true if file appears to be a calendar note, false otherwise
 */
export function detectCalendarFromFilename(path: string): boolean {
  const filename = path.split('/').pop() ?? ''
  // filenameToDate returns a Dayjs object for all calendar granularities.
  return Boolean(filenameToDate(filename).date)
}
